import { useCallback } from 'react';
import { Shortcut } from 'shortcuts';

import Flex from '../Flex';
import { Keyboard } from '../Badges';
import Text from '../Text';
import { useI18n, useModalManager, useShortcutManager } from '../../hooks';
import type { Translation } from '../../i18n';
import Modal from '../Modal';
import Table from '../Table';
import type { ColumnProps } from '../Table';

type TableMetaData = {
  id: string;
  action: string;
  command: JSX.Element;
}[];

// bindings structure is different for table shortcuts to support multiple bindings
// there are no such bindings in shortcut manager yet, but this may be moved there in future
const tableKeyBindings = {
  NavigateCells: {
    defaultKeyBinding: ['Up', 'Down', 'Left', 'Right'],
    keyBinding: ['Up', 'Down', 'Left', 'Right']
  },
  EnterTabNav: {
    defaultKeyBinding: ['Enter'],
    keyBinding: ['Enter']
  },
  TabNavigation: {
    defaultKeyBinding: ['Tab', 'Shift+Tab'],
    keyBinding: ['Tab', 'Shift+Tab']
  },
  RestoreGridNav: {
    defaultKeyBinding: ['Esc'],
    keyBinding: ['Esc']
  }
} as const;

export const KeyboardShortcut = ({ keyBinding }: { keyBinding: string }) => {
  return (
    <Flex container={{ gap: 0.5, alignItems: 'center' }}>
      {Shortcut.shortcut2accelerator(keyBinding)
        .split('+')
        .map(key => (
          <Keyboard keyName={key} key={key} />
        ))}
    </Flex>
  );
};

const GeneralKeyboardShortcuts = ({
  columns
}: {
  columns: ColumnProps<TableMetaData[number]>[];
}) => {
  const t = useI18n();
  const bindings = useShortcutManager();

  const tableData: TableMetaData = Object.entries(bindings).map(([actionId, { keyBinding }]) => {
    return {
      id: actionId,
      action: t(`shortcut_${actionId}` as keyof Translation),
      command: <KeyboardShortcut keyBinding={keyBinding} />
    };
  });

  return <Table title={t('general')} data={tableData} columns={columns} />;
};

const TableKeyboardShortcuts = ({ columns }: { columns: ColumnProps<TableMetaData[number]>[] }) => {
  const t = useI18n();

  const tableData: TableMetaData = Object.entries(tableKeyBindings).map(
    ([actionId, { keyBinding }]) => {
      return {
        id: actionId,
        action: t(`shortcut_${actionId}` as keyof Translation),
        command: (
          <Flex container={{ gap: 0.5, alignItems: 'center' }}>
            {keyBinding.map((singleBinding, index) => (
              <>
                <KeyboardShortcut keyBinding={singleBinding} />
                {/* Add comma except for last item */}
                {index < keyBinding.length - 1 && <Text>,</Text>}
              </>
            ))}
          </Flex>
        )
      };
    }
  );

  return <Table title={t('tables_and_grids')} data={tableData} columns={columns} />;
};

const KeyboardShortcutsModal = () => {
  const t = useI18n();
  const columns: ColumnProps<TableMetaData[number]>[] = [
    { renderer: 'action', label: t('action') },
    { renderer: 'command', label: t('key_combination') }
  ];
  return (
    <Modal heading={t('keyboard_commands')}>
      <Flex container={{ direction: 'column', gap: 4 }}>
        <GeneralKeyboardShortcuts columns={columns} />
        <TableKeyboardShortcuts columns={columns} />
      </Flex>
    </Modal>
  );
};

export const useKeyboardCommands = () => {
  const { create: createModal } = useModalManager();

  return useCallback(() => {
    createModal(KeyboardShortcutsModal, {}, { minimizable: true });
  }, [createModal]);
};

export default useKeyboardCommands;
